import { URL_CONST } from 'utils/urlConsts'

/**
 * Declarative navigation config — single source of truth for the side-nav tree.
 *
 * FULL PRODUCT-CENTRIC IA (from Figma node 1:26644 "Open nav -max features OPEN",
 * authoritative spec docs/plans/nav-ia-authoritative.md). The earlier "parity
 * seed" was WRONG: it reproduced the old flat top-nav items and HID un-entitled
 * items. The design renders the FULL structure always; capabilities the customer
 * is not entitled to render **locked** (padlock + greyed), NOT hidden. Only
 * truly-irrelevant nodes are hidden.
 *
 * LOCKED-VS-HIDDEN model (resolved in useResolvedNav.js):
 *   - A node with `requires` that passes  -> visible + unlocked.
 *   - A node with `requires` that fails:
 *       * `lockable: true`  -> visible + LOCKED (greyed + padlock, relevant capability).
 *       * otherwise         -> HIDDEN (irrelevant to this customer).
 *   - A node with NO `requires` is always visible + unlocked (known capability).
 *   - A node flagged `comingSoon: true` has no in-app route (Figma item that maps
 *     to no existing route); it renders inert. It may also be `lockable`.
 *   - A section is LOCKED when its whole product is unentitled (its own `requires`
 *     fails + `lockable`); its children are not independently surfaced while the
 *     section is locked. A sub-section can be locked under an unlocked section
 *     (e.g. Email Encryption, DMARC Management under Email Security).
 *
 * NavNode schema:
 * @typedef {'section'|'page'|'pageWithTabs'|'folder'} NavNodeType
 *   - `folder`: an L2 grouping inside Settings whose children are L3 pages. A
 *     folder resolves like a section (it never navigates; it is visible iff it
 *     has ≥1 visible child; it is active iff any child is active) but renders as
 *     an expand/collapse group inside the Settings sub-view rather than an L1 rail
 *     row. `decorateNode` recurses generically, so a folder needs no resolver
 *     special-casing — it is config + Settings-panel UI only.
 * @typedef {object} NavNode
 * @property {string} id
 * @property {NavNodeType} type
 * @property {string} label                 - hardcoded English (no i18n layer today)
 * @property {string} [icon]                 - DesignSystem/Icons export name, section/L1 only
 * @property {string} [path]                 - in-app destination
 * @property {string|string[]} [match]       - active-state path matching (defaults to path).
 * @property {NavNode[]} [children]
 * @property {object} [requires]             - single-source standard gating
 * @property {string[]} [requires.featureFlags]  - negatable, e.g. '!DISABLE_INVESTIGATION_TAB'
 * @property {string[]} [requires.permissions]
 * @property {string[]} [requires.addOns]        - ADDONS.*
 * @property {('o365'|'gws')[]} [requires.integrations]
 * @property {string} [requires.predicateId]     - compound/cross-source rule (navPredicates.js)
 * @property {('starter'|'campaignsOnly'|'ironscalesProtect')} [requires.atLeastPlan]
 * @property {boolean} [requires.partnerPresent] - partner exists & !isStaticSalesDemo (GroupSettings)
 * @property {'self'|'actual'} [entitlementScope] - single-source nodes only; default 'self'
 * @property {boolean} [lockable]            - when `requires` fails, render LOCKED instead of hidden
 * @property {string} [relevanceId]           - NAV_RELEVANCE key; locked ONLY when relevant to the
 *                                              customer's integration type/prereqs, else hidden
 * @property {string} [tag]                  - DS Tag type (TAGS_TYPES) shown after the label, e.g. 'BETA'
 * @property {boolean} [comingSoon]          - known capability with no in-app route yet (inert)
 * @property {object} [plg]                  - { path } locked-but-clickable PLG destination
 * @property {object} [href]                 - { external?, newTab?, hrefBuilderId? } dynamic dest
 * @property {boolean} [pinnedBottom]        - Settings
 * @property {boolean} [highlight]           - Settings "highlight" page surfaced in the
 *                                              collapsed-hover flyout ("Settings highlight
 *                                              pages", Confluence Navigation Menu spec)
 * @property {'bell'} [badge]                - notification eligibility (resolved at runtime)
 * @property {boolean} [noRestrictDisable]   - never disabled by access-restriction (legacy parity)
 */

/**
 * Permission set shared by Incidents + Investigation (irontraps roles).
 */
const IRONTRAPS_ROLES = ['admin', 'irontraps_action', 'manage_irontraps', 'irontraps_viewer']

/**
 * Pages the mobile rail may navigate to. Everything else is disabled — those
 * surfaces are not supported below the platform-nav mobile breakpoint
 * (IC-49684 #2). Incidents Status is the one exception.
 */
export const MOBILE_NAV_ALLOWED_PAGE_IDS = new Set(['incidents'])

export const isMobileNavPageAllowed = (nodeId) => MOBILE_NAV_ALLOWED_PAGE_IDS.has(nodeId)

/**
 * @type {NavNode[]}
 */
export const navConfig = [
  // ── 1. Dashboards ───────────────────────────────────────────────────────────
  {
    id: 'dashboard',
    type: 'section',
    label: 'Dashboards',
    icon: 'Dashboard',
    children: [
      {
        // Security Overview — the home dashboard (rail "Overview", /dashboard).
        // exact match: /dashboard is a prefix of the sibling dashboard routes.
        id: 'security-overview',
        type: 'page',
        label: 'Security Overview',
        path: URL_CONST.dashboard.home,
        match: URL_CONST.dashboard.home,
        exact: true,
        // Hidden without an IronTraps role. Not lockable.
        requires: { permissions: IRONTRAPS_ROLES },
        entitlementScope: 'self',
        relevanceId: 'IRONTRAPS',
      },
      {
        // Incident Summary — the mitigation dashboard. Hidden without an IronTraps role.
        id: 'incident-summary',
        type: 'page',
        label: 'Incident Summary',
        path: URL_CONST.dashboard.mitigation,
        match: URL_CONST.dashboard.mitigation,
        requires: { permissions: IRONTRAPS_ROLES },
        entitlementScope: 'self',
        relevanceId: 'IRONTRAPS',
      },
      {
        // Awareness dashboard — existing route.
        id: 'dashboard-awareness',
        type: 'page',
        label: 'Awareness',
        path: URL_CONST.dashboard.awareness,
        match: URL_CONST.dashboard.awareness,
      },
      {
        // ATO Overview — /dashboard/ato. Account Takeover also links here as
        // "Overview" (ato-overview) until that entry replaces this one at GA.
        id: 'dashboard-ato',
        type: 'page',
        label: 'ATO',
        path: URL_CONST.dashboard.ato,
        match: URL_CONST.dashboard.ato,
        requires: { predicateId: 'ATO_DASHBOARD' },
      },
      {
        // INTERIM (IC-48828 #17 pending product): SOC Agent dashboard — same
        // orphaned-rail situation as the retired ADE entry. Gated on the dashboard FF AND the
        // per-company phishSoc billable toggle (same shape as PSIMA_AGENT /
        // RED_TEAM_AGENT); the legacy FF-only gate showed the dashboard to
        // companies whose SOC agent is disabled.
        id: 'soc-agent',
        type: 'page',
        label: 'SOC Agent',
        path: URL_CONST.agents.socDashboard,
        match: URL_CONST.agents.socDashboard,
        requires: { predicateId: 'SOC_AGENT' },
      },
    ],
  },

  // ── 2. Email Security ─────────────────────────────────────────────────────────
  {
    id: 'email-security',
    type: 'section',
    label: 'Email Security',
    icon: 'Mailbox',
    children: [
      {
        // selectIncidentsVisibility: irontraps perms + atLeastStarterPlan (self)
        id: 'incidents',
        type: 'page',
        label: 'Incidents Status',
        path: URL_CONST.incidents.unclassified,
        // Scoped to the incidents tabs + their drill-downs only: `incidents.self`
        // ('/irontraps') is a prefix of every irontraps route, which falsely
        // activated this node alongside its siblings 'reports'
        // (/irontraps/history/reported-incidents) and 'scan-back'
        // (/irontraps/scanback/overview).
        // Named status tabs only — `incidents` (`/irontraps/incidents`) is a
        // prefix of the Scan Back incidents list (`/irontraps/incidents/scanback`)
        // and would steal the active indicator when that internal tab is open.
        match: [
          URL_CONST.incidents.unclassified,
          URL_CONST.incidents.classified,
          URL_CONST.incidents.challenged,
          URL_CONST.incidents.attackdetails,
          URL_CONST.incidents.serverscan,
        ],
        badge: 'bell',
        // Role is in requires and relevance so a role miss hides. Plan miss locks.
        requires: { permissions: IRONTRAPS_ROLES, atLeastPlan: 'starter' },
        entitlementScope: 'self',
        relevanceId: 'IRONTRAPS',
        lockable: true,
      },
      {
        // selectInvestigationVisibility: cross rule (plan + permission + FF-negation
        // kill-switch) -> predicate. Carries a plan gate (atLeastIronscalesProtect),
        // so it LOCKS like incidents/reports per spec 1888223235 ("Lock menu items").
        id: 'investigation',
        type: 'page',
        label: 'Email Investigation',
        path: URL_CONST.investigation.threatLog,
        match: URL_CONST.investigation.threatLog,
        // Email Investigation lives at '/threat-activity', which is a prefix of ATO's
        // '/threat-activity/account-investigation'. Without an exact match both this
        // section and Account Take Over light up on the ATO page — pin it to exact.
        exact: true,
        // Role and kill switch hide via NAV_RELEVANCE.INVESTIGATION. Plan miss locks.
        requires: { predicateId: 'INVESTIGATION' },
        relevanceId: 'INVESTIGATION',
        lockable: true,
      },
      {
        // Accounts investigation for tenants whose Account Takeover section is absent
        // (the new nav hides the page's own sidebar). ATO tenants reach it via
        // Account Takeover › Users, so this entry hides for them.
        id: 'accounts-investigation',
        type: 'page',
        label: 'Accounts',
        path: URL_CONST.investigation.ato,
        match: URL_CONST.investigation.ato,
        requires: { predicateId: 'ACCOUNTS_INVESTIGATION' },
        relevanceId: 'ACCOUNTS_INVESTIGATION',
        lockable: true,
      },
      {
        // Incident Reports — existing incidents reported-history route
        // (in-page tabs Reported/Remediated).
        id: 'reports',
        type: 'page',
        label: 'Incident Reports',
        path: URL_CONST.incidents.reported,
        match: [URL_CONST.incidents.reported, URL_CONST.incidents.remediated],
        requires: { permissions: IRONTRAPS_ROLES, atLeastPlan: 'starter' },
        entitlementScope: 'self',
        relevanceId: 'IRONTRAPS',
        lockable: true,
      },
      {
        // Red Teaming AI Agent — exists today behind ENABLE_RED_TEAM (routed at
        // /red-team, RootContainer.js); relocated by crosswalk 2157576211, not
        // net-new. Gated on the FF AND the per-company redAgent billable toggle
        // (same shape as PSIMA_AGENT; the old nav's selectRedTeamVisibility also
        // checks both) — HIDDEN when either is off; locking is reserved for
        // plan/add-on entitlements per spec 1888223235, matching SOC.
        id: 'red-team',
        type: 'page',
        label: 'Red Teaming Agent',
        path: URL_CONST.redTeam.self,
        match: URL_CONST.redTeam.self,
        requires: { predicateId: 'RED_TEAM_AGENT' },
      },
      {
        // Moved out of Settings by IC-50849 with its gate unchanged.
        id: 'vendor-base',
        type: 'page',
        label: 'Vendor Base',
        path: URL_CONST.vendorBase.self,
        match: URL_CONST.vendorBase.self,
        requires: { predicateId: 'VENDOR_PROTECTION' },
      },
      {
        // DMARC Management — self admin + region!=CA gate; magic-link destination when
        // the actual company has the DMARC_MANAGEMENT add-on. Lockable sub-section.
        id: 'dmarc',
        type: 'page',
        label: 'DMARC Management',
        path: URL_CONST.dmarc.self,
        match: URL_CONST.dmarc.self,
        requires: { predicateId: 'DMARC' },
        relevanceId: 'DMARC',
        href: { hrefBuilderId: 'DMARC' },
        plg: { path: URL_CONST.dmarc.self },
        lockable: true,
      },
      {
        // Scan Back — existing scanback overview + incidents-list routes under
        // irontraps. The page redirects away when the actual company has no
        // scanback status, so entitlement (SCANBACK predicate) includes that
        // status — an entitled tenant without scanback is HIDDEN, not a dead
        // link (IC-49684 #3). Unentitled + scanback active still LOCKS.
        id: 'scan-back',
        type: 'page',
        label: 'Scan Back',
        path: URL_CONST.incidents.scanbackOverview,
        match: [URL_CONST.incidents.scanbackOverview, URL_CONST.incidents.scanback],
        requires: { predicateId: 'SCANBACK' },
        relevanceId: 'SCANBACK',
        lockable: true,
      },
    ],
  },

  // ── 3. Email Encryption (lockable section) ──────────────────────────────────────
  {
    // Email Encryption — its own top-level section (crosswalk 2157576211): locked
    // for O365 tenants without the Encryption add-on (click → PLG page), hidden for
    // non-O365 (GWS) tenants — same shape as Deepfake. Self admin gate.
    id: 'encryption',
    type: 'section',
    label: 'Email Encryption',
    icon: 'Encryption',
    requires: { predicateId: 'EMAIL_ENCRYPTION_PRODUCT' },
    relevanceId: 'EMAIL_ENCRYPTION',
    plg: { path: URL_CONST.encryptionPLG.self },
    lockable: true,
    children: [
      {
        // Activity — the existing OES email-tracking page.
        id: 'encryption-activity',
        type: 'page',
        label: 'Activity',
        path: URL_CONST.emailEncryption.emailTracking,
        match: URL_CONST.emailEncryption.emailTracking,
      },
      {
        // Encryption Settings — link to the global Settings › Email Encryption ›
        // General Settings page (same pattern as Deepfake Settings / S&T Settings:
        // this rail item lights up while the user is on that settings page).
        id: 'encryption-settings',
        type: 'page',
        label: 'Encryption Settings',
        path: URL_CONST.settings.emailSettings,
        match: [
          URL_CONST.settings.emailSettings,
          URL_CONST.settings.emailCompanyPolicies,
          URL_CONST.settings.emailBranding,
        ],
      },
    ],
  },

  // ── 4. Account Takeover (lockable section) ──────────────────────────────────────
  {
    id: 'account-take-over',
    type: 'section',
    label: 'Account Takeover',
    icon: 'FillAto',
    requires: { predicateId: 'ATO' },
    relevanceId: 'ATO',
    lockable: true,
    children: [
      {
        // Overview — same page as Dashboard › ATO (/dashboard/ato). Kept in both
        // places until GA (Eran: dashboard label stays "ATO", this one is
        // "Overview"). Same gate as dashboard-ato. Both nodes highlight on
        // that route, matching the S&T Settings duplicate-active pattern.
        id: 'ato-overview',
        type: 'page',
        label: 'Overview',
        path: URL_CONST.dashboard.ato,
        match: URL_CONST.dashboard.ato,
        requires: { predicateId: 'ATO_DASHBOARD' },
      },
      {
        // Incidents — same Resolution Center list as Email Security › Incidents
        // Status, scoped to ATO rows (quickFilter ATO). Distinct path so this
        // node (not Email Security) stays active.
        id: 'ato-incidents',
        type: 'page',
        label: 'Incidents',
        path: URL_CONST.incidents.atoUnclassified,
        match: URL_CONST.incidents.atoIncidents,
        requires: { permissions: IRONTRAPS_ROLES, atLeastPlan: 'starter' },
        entitlementScope: 'self',
        lockable: true,
      },
      {
        // Users — the existing account-investigation (accounts) page.
        id: 'ato-users',
        type: 'page',
        label: 'Users',
        path: URL_CONST.investigation.ato,
        match: URL_CONST.investigation.ato,
      },
      {
        // Settings — existing Settings › Threat protection › Account Takeover
        // page (same pattern as S&T Settings → /settings/simulator).
        // id stays `ato-settings`: the label "Settings" is shared with the
        // global Settings section and the Deepfake settings leaf. Active state
        // matches by path, not by label.
        id: 'ato-settings',
        type: 'page',
        label: 'Settings',
        path: URL_CONST.settings.accountTakeover,
        match: URL_CONST.settings.accountTakeover,
      },
    ],
  },

  // ── 5. Deepfake Protection (lockable section) ───────────────────────────────────
  {
    id: 'deepfake',
    type: 'section',
    label: 'Deepfake Protection',
    icon: 'Deepfake',
    requires: { predicateId: 'DEEPFAKE' },
    relevanceId: 'DEEPFAKE',
    // Locked click → existing deepfake PLG page (same destination the legacy
    // PLGDF promo link used).
    plg: { path: URL_CONST.deepfake.self },
    lockable: true,
    children: [
      {
        // Meetings Dashboard — Deepfake meetings route.
        id: 'deepfake-meetings',
        type: 'page',
        label: 'Meetings Dashboard',
        path: URL_CONST.deepfake.meetings,
        match: URL_CONST.deepfake.meetings,
      },
      {
        // Protected Profiles — Deepfake profiles route.
        id: 'deepfake-profiles',
        type: 'page',
        label: 'Protected Profiles',
        path: URL_CONST.deepfake.profiles,
        match: URL_CONST.deepfake.profiles,
      },
      {
        // Deepfake Settings — navigates to the deepfake settings page under
        // /settings (crosswalk 2157576211: "Navigates to Settings › Deepfake").
        id: 'deepfake-settings',
        type: 'page',
        label: 'Deepfake Settings',
        path: URL_CONST.settings.deepFakeGeneral,
        match: URL_CONST.settings.deepFakeGeneral,
      },
    ],
  },

  // ── 6. Human Risk Management (lockable section) ─────────────────────────────────
  {
    id: 'hrm',
    type: 'section',
    label: 'Human Risk Management',
    icon: 'Awareness',
    requires: { predicateId: 'HRM' },
    // Locked click opens Awareness Campaigns. Other roles are hidden via relevance.
    relevanceId: 'HRM',
    plg: { path: URL_CONST.awarenessCenter.campaigns },
    lockable: true,
    children: [
      {
        // Phishing Simulation Agent — /psima (in-page tabs Dashboard/Launched/Settings).
        // Same gate as the old nav (PSIMA_AGENT): campaign role plus the actual
        // company's FF + psimaAgent toggle, or partner.has_psima_companies.
        // Not a plan/add-on, so HIDDEN (not locked) when unentitled — the HRM
        // section itself carries the plan/add-on lock.
        id: 'ps-agent',
        type: 'page',
        label: 'PS Agent',
        path: URL_CONST.psima.self,
        match: URL_CONST.psima.self,
        requires: { predicateId: 'PSIMA_AGENT' },
      },
      {
        // Campaigns, wizard, and campaign report. Not awarenessCenter.self (catalog prefix).
        id: 'awareness-campaigns',
        type: 'page',
        label: 'Awareness Campaigns',
        path: URL_CONST.awarenessCenter.campaigns,
        match: [
          URL_CONST.awarenessCenter.campaigns,
          URL_CONST.awarenessCenter.wizardCampaign,
          URL_CONST.awarenessCenter.report,
          '/campaigns/',
        ],
      },
      {
        // Content Catalog — Landing Pages / Scenarios / Call-for-action live under
        // awareness-center as in-page tabs (added separately). The nav node lands on
        // Landing Pages and stays active across all three routes (crosswalk 2157576211).
        id: 'content-management',
        type: 'page',
        label: 'Content Catalog',
        path: URL_CONST.awarenessCenter.landingpage,
        match: [
          URL_CONST.awarenessCenter.landingpage,
          URL_CONST.awarenessCenter.scenarios,
          URL_CONST.awarenessCenter.callforaction,
        ],
      },
      {
        // S&T Settings — navigates into the existing Settings › Simulation & Training
        // page (mirrors 'deepfake-settings' → /settings). The duplicate-active with
        // 'settings-simulation-training' on the same route is expected (crosswalk
        // 2157576211), matching the deepfake-settings pattern.
        id: 'st-settings',
        type: 'page',
        label: 'S&T Settings',
        path: URL_CONST.settings.simulator,
        match: [
          URL_CONST.settings.simulator,
          URL_CONST.settings.ignoreIpRange,
          URL_CONST.settings.psima,
        ],
        exact: true,
      },
    ],
  },

  // ── 7. Settings (pinned bottom) ─────────────────────────────────────────────────
  // Full Settings IA per Confluence "Navigation Menu" (page 1856208905). The
  // Settings section renders in the upward sub-view (SettingsPanelView), NOT the
  // L1 rail. Direct pages render as rows; `folder` nodes render as expandable
  // groups whose children are L3 pages. In-page tabs are NOT nav nodes.
  //
  // `highlight: true` marks the "Settings highlight pages" (⭐) surfaced in the
  // collapsed-hover flyout alongside "View all settings".
  //
  // Leaf paths map to existing URL_CONST.settings.* routes where one exists; leaves
  // with no route yet are `comingSoon: true` (inert + "Soon" tag). All in-app paths
  // sit under /settings (URL_CONST.settings.self) or a declared route, so AC13
  // route-resolution passes by prefix.
  {
    id: 'settings',
    type: 'section',
    label: 'Settings',
    icon: 'CompanySettings',
    pinnedBottom: true,
    children: [
      {
        // Profile — direct page (settings/account/profile).
        id: 'settings-profile',
        type: 'page',
        label: 'Profile',
        path: URL_CONST.settings.profile,
        match: URL_CONST.settings.profile,
      },
      {
        // Account — folder. Organization (3 in-page tabs), Activities, Resources.
        id: 'settings-account',
        type: 'folder',
        label: 'Account',
        children: [
          {
            // Organization — page w/ tabs General & Security / Permissions / Languages.
            id: 'settings-organization',
            type: 'page',
            label: 'Organization',
            path: URL_CONST.settings.general,
            match: [
              URL_CONST.settings.general,
              URL_CONST.settings.permissions,
              URL_CONST.settings.languages,
            ],
          },
          {
            // Activities — legacy gate mixes self+actual scope (selectUserDropdownItems,
            // containers/Navigation/selectors.js): self admin AND actual plan exists &&
            // actual plan !== STARTER -> compound predicate. Not lockable -> hidden when
            // failing, matching the legacy dropdown behavior.
            id: 'settings-activities',
            type: 'page',
            label: 'Activities',
            path: URL_CONST.activities.self,
            match: URL_CONST.activities.self,
            requires: { predicateId: 'ACTIVITIES' },
          },
          {
            id: 'settings-resources',
            type: 'page',
            label: 'Resources',
            path: URL_CONST.resources.self,
            match: URL_CONST.resources.self,
          },
          {
            // Mailboxes — the live mailbox-management page, moved under Settings by
            // crosswalk 2157576211. Gate mirrors selectMailboxesVisibility (self
            // admin); the legacy link is never disabled by access-restriction.
            id: 'mailboxes',
            type: 'page',
            label: 'Mailboxes',
            path: URL_CONST.mailboxes.management,
            match: URL_CONST.mailboxes.self,
            requires: { permissions: ['admin'] },
            entitlementScope: 'self',
            noRestrictDisable: true,
          },
        ],
      },
      {
        // ⭐ AI features — folder. AI credits (tabs Overview/History/Billing).
        id: 'settings-ai-features',
        type: 'folder',
        label: 'AI Features',
        highlight: true,
        children: [
          {
            id: 'settings-ai-credits',
            type: 'page',
            label: 'AI Credits',
            path: URL_CONST.settings.creditsSettings,
            match: URL_CONST.settings.creditsSettings,
          },
        ],
      },
      {
        // ⭐ Threat protection — folder. Detection & Response, Report Phishing Add-on,
        // Allow list (each a page w/ in-page tabs).
        id: 'settings-threat-protection',
        type: 'folder',
        label: 'Threat Protection',
        highlight: true,
        children: [
          {
            id: 'settings-detection-response',
            type: 'page',
            label: 'Detection & Response',
            path: URL_CONST.settings.incidence,
            match: [
              URL_CONST.settings.incidence,
              URL_CONST.settings.community,
              URL_CONST.settings.impersonation,
              URL_CONST.settings.malware,
            ],
          },
          {
            // Report Phishing Add-on — page w/ tabs Integration / Customizations.
            // Land on the O365 Customizations tab (PM: /settings/threats/customization/o365).
            id: 'settings-report-phishing',
            type: 'page',
            label: 'Report Phishing Add-on',
            path: URL_CONST.settings.customizationO365,
            match: [URL_CONST.settings.customization, URL_CONST.settings.customizationO365],
          },
          {
            id: 'settings-allow-list',
            type: 'page',
            label: 'Allow List',
            path: URL_CONST.settings.allowlist,
            match: URL_CONST.settings.allowlist,
          },
          {
            // Hidden without the ATO add-on (o365 + ato). Not lockable — QA:
            // the settings leaf must not appear for a company that does not
            // have ATO (the locked upsell already lives on the ATO section).
            id: 'settings-account-takeover',
            type: 'page',
            label: 'Account Takeover',
            path: URL_CONST.settings.accountTakeover,
            match: URL_CONST.settings.accountTakeover,
            requires: { predicateId: 'ATO' },
          },
        ],
      },
      {
        // ⭐ Integrations — folder (crosswalk 2157576211). Email provider is a
        // page w/ tabs (Email Server / MS Defender). Alerts & Logging is its
        // own leaf. Explicit leaf matches, NOT a /settings/integrations prefix:
        // deepfakeIntegration and malware (External link scanning under
        // Detection & Response) share that prefix but belong to other nodes.
        id: 'settings-integrations',
        type: 'folder',
        label: 'Integrations',
        highlight: true,
        children: [
          {
            id: 'settings-email-provider',
            type: 'page',
            label: 'Email Provider',
            path: URL_CONST.settings.emailServer,
            match: [URL_CONST.settings.emailServer, URL_CONST.settings.microsoftQuarantine],
          },
          {
            id: 'settings-alerts-logging',
            type: 'page',
            label: 'Alerts & Logging',
            path: URL_CONST.settings.alerts,
            match: URL_CONST.settings.alerts,
          },
          {
            id: 'settings-security-event-forwarding',
            type: 'page',
            label: 'Security Event Forwarding',
            path: URL_CONST.settings.alertsV2,
            match: URL_CONST.settings.alertsV2,
            tag: 'BETA',
            requires: { predicateId: 'SETTINGS_SEF' },
          },
        ],
      },
      {
        // Notifications — folder (crosswalk 2157576211). Alerts is a page w/
        // tabs (System / End-User). Collaboration Apps is a direct page.
        id: 'settings-notifications',
        type: 'folder',
        label: 'Notifications',
        children: [
          {
            id: 'settings-notifications-alerts',
            type: 'page',
            label: 'Alerts',
            path: URL_CONST.settings.systemAlerts,
            match: [URL_CONST.settings.systemAlerts, URL_CONST.settings.endUserAlerts],
          },
          {
            id: 'settings-collaboration-apps',
            type: 'page',
            label: 'Collaboration Apps',
            path: URL_CONST.settings.collaborationApps,
            match: URL_CONST.settings.collaborationApps,
          },
        ],
      },
      {
        // Simulation & Training. Hidden without S&T. The HRM section carries the upsell.
        id: 'settings-simulation-training',
        type: 'page',
        label: 'Simulation & Training',
        path: URL_CONST.settings.simulator,
        match: [
          URL_CONST.settings.simulator,
          URL_CONST.settings.ignoreIpRange,
          URL_CONST.settings.psima,
        ],
        requires: { predicateId: 'SETTINGS_SIMULATION_TRAINING' },
        exact: true,
      },
      {
        // Deepfake Protection — folder. Integration + Settings (existing routes).
        // Crosswalk 2157576211: gated on the DEEPFAKE_SERVICE feature flag (actual).
        // Folders never lock -> hidden when the FF is off. `requires` on a folder is
        // resolved generically by decorateNode (same recursion as a section).
        id: 'settings-deepfake',
        type: 'folder',
        label: 'Deepfake Protection',
        requires: { featureFlags: ['DEEPFAKE_SERVICE'] },
        entitlementScope: 'actual',
        children: [
          {
            id: 'settings-deepfake-integration',
            type: 'page',
            label: 'Integration',
            path: URL_CONST.settings.deepfakeIntegration,
            match: URL_CONST.settings.deepfakeIntegration,
          },
          {
            id: 'settings-deepfake-settings',
            type: 'page',
            label: 'Settings',
            path: URL_CONST.settings.deepFakeGeneral,
            match: URL_CONST.settings.deepFakeGeneral,
          },
        ],
      },
      {
        // Email Encryption — folder. Integration + General Settings (existing routes).
        // Crosswalk 2157576211: gated on the Email Encryption add-on. Shares the
        // EMAIL_ENCRYPTION predicate with the Email Security node so the product and
        // its settings can never disagree about who is entitled; the old nav gated
        // this same folder on the same field (components/pages/settings/index.js).
        // Folders never lock -> hidden without the add-on (the locked upsell already
        // exists under Email Security). `requires` on a folder resolves generically.
        // General Settings is one page with tabs: Configuration / Company Policies /
        // Branding — Branding is no longer a separate nav child (crosswalk 2157576211).
        id: 'settings-email-encryption',
        type: 'folder',
        label: 'Email Encryption',
        requires: { predicateId: 'EMAIL_ENCRYPTION' },
        children: [
          {
            id: 'settings-encryption-integration',
            type: 'page',
            label: 'Integration',
            path: URL_CONST.settings.emailIntegration,
            match: URL_CONST.settings.emailIntegration,
          },
          {
            id: 'settings-encryption-settings',
            type: 'page',
            label: 'General Settings',
            path: URL_CONST.settings.emailSettings,
            match: [
              URL_CONST.settings.emailSettings,
              URL_CONST.settings.emailCompanyPolicies,
              URL_CONST.settings.emailBranding,
            ],
          },
        ],
      },
      {
        // MSP Settings — PARTNER-ONLY folder (reuse the group-settings partner gate:
        // admin + partner present + !isStaticSalesDemo). Group Settings + PSA.
        id: 'settings-msp',
        type: 'folder',
        label: 'MSP Settings',
        requires: { permissions: ['admin'], partnerPresent: true },
        entitlementScope: 'self',
        children: [
          {
            id: 'settings-group-settings',
            type: 'page',
            label: 'Group Settings',
            path: URL_CONST.groupSettings.self,
            match: URL_CONST.groupSettings.self,
          },
          {
            id: 'settings-psa',
            type: 'page',
            label: 'PSA',
            path: URL_CONST.settings.partner,
            match: URL_CONST.settings.partner,
          },
        ],
      },
    ],
  },
]

export default navConfig
